import { useEffect, useState } from "react";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, ChevronDown, ChevronRight, Trash2, Upload } from "lucide-react";

const fileUrl = (p) => (p ? (p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}${p}`) : null);

const Field = ({ label, children }) => (
  <label className="block text-xs">
    <span className="mb-0.5 block font-bold text-muted-foreground">{label}</span>
    {children}
  </label>
);
const inp = "w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm";

const PackHorses = ({ pack }) => {
  const [horses, setHorses] = useState(null);
  const [nh, setNh] = useState({ display_name: "", coat_name: "", base_color: "", pattern: "Solid", color_hex: "#8a5a2b", rarity: "Rare", weight: 1 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await gameApi.get(`/game/admin/premium-packs/${pack.id}/horses`);
    setHorses(data.horses || []);
  };
  useEffect(() => { load(); }, [pack.id]); // eslint-disable-line

  const addHorse = async () => {
    if (!nh.display_name.trim()) return toast.error("Give the horse a name");
    setBusy(true);
    try {
      await gameApi.post(`/game/admin/premium-packs/${pack.id}/horses`, nh);
      setNh({ display_name: "", coat_name: "", base_color: "", pattern: "Solid", color_hex: "#8a5a2b", rarity: "Rare", weight: 1 });
      toast.success("Horse added — now upload its coat image");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const upload = async (art_id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await gameApi.post(`/game/admin/horse-art/${art_id}/image`, fd);
      toast.success("Coat image uploaded");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const del = async (art_id) => {
    try {
      await gameApi.delete(`/game/admin/premium-packs/${pack.id}/horses/${art_id}`);
      toast.success("Horse removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div className="space-y-2 border-t border-border p-3">
      {horses === null ? <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="animate-spin" size={14} /> Loading horses…</div>
        : (
          <>
            {horses.length === 0 && <p className="text-xs text-muted-foreground">No horses yet. Add one below, then upload its coat image so it can generate.</p>}
            {horses.map((h) => (
              <div key={h.id} data-testid={`pack-horse-${h.id}`} className="flex items-center gap-2 rounded-lg border border-border bg-background px-2 py-1.5 text-xs">
                <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-muted">
                  {h.image_path ? <img src={fileUrl(h.image_path)} alt="" className="h-full w-full object-contain" /> : <span className="text-[9px] text-muted-foreground">no art</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{h.display_name}</p>
                  <p className="text-[11px] text-muted-foreground">{h.coat_name || h.base_color} · {h.pattern}
                    {h.ready ? <span className="ml-1 rounded bg-green-100 px-1 font-bold text-green-700">ready</span>
                      : <span className="ml-1 rounded bg-amber-100 px-1 font-bold text-amber-700">needs image</span>}</p>
                </div>
                <label className="flex shrink-0 cursor-pointer items-center gap-1 rounded-lg border border-border px-2 py-1 font-bold text-primary hover:bg-muted">
                  <Upload size={12} /> {h.image_path ? "Replace" : "Upload"}
                  <input data-testid={`pack-horse-upload-${h.id}`} type="file" accept="image/png,image/webp" className="hidden" onChange={(e) => upload(h.id, e.target.files?.[0])} />
                </label>
                <button data-testid={`pack-horse-del-${h.id}`} onClick={() => del(h.id)} className="shrink-0 rounded-lg p-1 text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></button>
              </div>
            ))}
            <div className="grid grid-cols-2 gap-2 rounded-lg border border-dashed border-border p-2 sm:grid-cols-3">
              <Field label="Horse / coat name"><input data-testid="pack-horse-name" className={inp} value={nh.display_name} onChange={(e) => setNh({ ...nh, display_name: e.target.value })} /></Field>
              <Field label="Coat color"><input className={inp} value={nh.coat_name} onChange={(e) => setNh({ ...nh, coat_name: e.target.value })} placeholder="e.g. Bay Roan" /></Field>
              <Field label="Pattern"><input className={inp} value={nh.pattern} onChange={(e) => setNh({ ...nh, pattern: e.target.value })} /></Field>
              <Field label="Hex"><input className={inp} type="color" value={nh.color_hex} onChange={(e) => setNh({ ...nh, color_hex: e.target.value })} /></Field>
              <Field label="Rarity"><input className={inp} value={nh.rarity} onChange={(e) => setNh({ ...nh, rarity: e.target.value })} /></Field>
              <Field label="Weight"><input className={inp} type="number" step="0.1" value={nh.weight} onChange={(e) => setNh({ ...nh, weight: parseFloat(e.target.value) || 1 })} /></Field>
              <button data-testid="pack-horse-add" disabled={busy} onClick={addHorse} className="col-span-2 flex items-center justify-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground sm:col-span-3">
                <Plus size={14} /> Add horse to pack
              </button>
            </div>
          </>
        )}
    </div>
  );
};

const PackCard = ({ pack, onChange }) => {
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState({ name: pack.name, hma: pack.hma, body_model: pack.body_model || "" });
  const [saving, setSaving] = useState(false);
  const set = async (patch) => { try { await gameApi.put(`/game/admin/premium-packs/${pack.id}`, patch); onChange(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const saveMeta = async () => {
    if (!meta.name.trim() || !meta.hma.trim()) return toast.error("Pack name and HMA are required");
    setSaving(true);
    try {
      await gameApi.put(`/game/admin/premium-packs/${pack.id}`, { name: meta.name.trim(), hma: meta.hma.trim(), body_model: meta.body_model.trim() });
      toast.success(meta.body_model.trim() ? `Saved — body model set to "${meta.body_model.trim()}"` : "Saved — body model reset to the pack's unique id");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
  };
  const dirty = meta.name !== pack.name || meta.hma !== pack.hma || meta.body_model !== (pack.body_model || "");
  return (
    <div data-testid={`pack-card-${pack.collection}`} className="rounded-xl border border-border bg-card">
      <div className="flex items-center gap-2 px-3 py-2">
        <button onClick={() => setOpen(!open)} className="flex flex-1 items-center gap-2 text-left">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="font-bold text-primary">{pack.name}</span>
          <span className="text-xs text-muted-foreground">[{pack.hma}]</span>
          <span className="text-[11px] text-muted-foreground">{pack.ready_count}/{pack.horse_count} ready · {pack.owned_count} owners</span>
        </button>
        <label className="flex items-center gap-1 text-xs font-bold">
          <input data-testid={`pack-active-${pack.collection}`} type="checkbox" checked={pack.active} onChange={(e) => set({ active: e.target.checked })} />
          {pack.active ? <span className="text-green-700">Live</span> : <span className="text-muted-foreground">Off</span>}
        </label>
      </div>
      {open && (
        <>
          <div className="space-y-2 border-t border-border px-3 py-2">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              <Field label="Pack name"><input data-testid={`pack-edit-name-${pack.collection}`} className={inp} value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} /></Field>
              <Field label="HMA name (locks horses)"><input data-testid={`pack-edit-hma-${pack.collection}`} className={inp} value={meta.hma} onChange={(e) => setMeta({ ...meta, hma: e.target.value })} /></Field>
              <Field label="Body model"><input data-testid={`pack-edit-body-${pack.collection}`} className={inp} value={meta.body_model} onChange={(e) => setMeta({ ...meta, body_model: e.target.value })} placeholder={pack.collection} /></Field>
            </div>
            <p className="text-[11px] text-muted-foreground">Current body model: <b className={pack.body_model === "standard" ? "text-rose-600" : "text-primary"}>{pack.body_model || pack.collection}</b>. Give each pack a <b>unique</b> body model (blank = the pack's own id) so its HMA art doesn't collide with other packs. Changing it re-labels this pack's coat art only — <b>ownership and horses are kept</b>.</p>
            <button data-testid={`pack-save-meta-${pack.collection}`} disabled={!dirty || saving} onClick={saveMeta} className="rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground disabled:opacity-40">{saving ? "Saving…" : "Save details"}</button>
          </div>
          <div className="grid grid-cols-2 gap-2 px-3 pb-2 sm:grid-cols-4">
            <Field label="Appearance rate"><input className={inp} type="number" step="0.01" defaultValue={pack.appearance_rate} onBlur={(e) => set({ appearance_rate: parseFloat(e.target.value) || 0 })} /></Field>
            <Field label="Max per selection"><input className={inp} type="number" defaultValue={pack.max_per_selection} onBlur={(e) => set({ max_per_selection: parseInt(e.target.value) || 1 })} /></Field>
            <Field label="Allow duplicate coats"><select className={inp} defaultValue={pack.allow_dupe_coats ? "1" : "0"} onChange={(e) => set({ allow_dupe_coats: e.target.value === "1" })}><option value="0">No</option><option value="1">Yes</option></select></Field>
            <Field label="Prices"><div className="pt-1 text-[11px] text-muted-foreground">{pack.price_cents ? `$${(pack.price_cents / 100).toFixed(2)}` : "no $"} · {pack.coin_price ? `${pack.coin_price.toLocaleString()}🪙` : "no coins"}<br /><span className="text-[10px]">Edit prices in Premium tab</span></div></Field>
          </div>
          <PackHorses pack={pack} />
        </>
      )}
    </div>
  );
};

export const PremiumPacksAdmin = () => {
  const [packs, setPacks] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [np, setNp] = useState({ name: "", hma: "", body_model: "", usd: "", coin_price: 250000, appearance_rate: 0.12, max_per_selection: 2, allow_dupe_coats: false, active: false });
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await gameApi.get("/game/admin/premium-packs"); setPacks(data.packs || []); };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const create = async () => {
    if (!np.name.trim() || !np.hma.trim()) return toast.error("Pack name and HMA are required");
    setBusy(true);
    try {
      await gameApi.post("/game/admin/premium-packs", {
        name: np.name, hma: np.hma, body_model: np.body_model || null,
        price_cents: np.usd ? Math.round(parseFloat(np.usd) * 100) : null,
        coin_price: np.coin_price ? parseInt(np.coin_price) : null,
        appearance_rate: parseFloat(np.appearance_rate) || 0.12,
        max_per_selection: parseInt(np.max_per_selection) || 2,
        allow_dupe_coats: !!np.allow_dupe_coats, active: !!np.active,
      });
      toast.success("Pack created — add horses, then flip it Live");
      setShowNew(false);
      setNp({ name: "", hma: "", body_model: "", usd: "", coin_price: 250000, appearance_rate: 0.12, max_per_selection: 2, allow_dupe_coats: false, active: false });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create pack"); }
    setBusy(false);
  };

  return (
    <div className="space-y-3" data-testid="premium-packs-admin">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-base font-bold text-primary">Premium Horse Packs</h3>
        <button data-testid="pack-new-btn" onClick={() => setShowNew(!showNew)} className="flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground"><Plus size={14} /> New Pack</button>
      </div>
      <p className="text-xs text-muted-foreground">Create a pack, add horses (upload a coat image per horse), set the appearance rate, then flip it <b>Live</b>. Players who buy the pack (coins or real money) will randomly find these horses, locked to the pack's HMA — just like the built-in packs.</p>

      {showNew && (
        <div className="grid grid-cols-2 gap-2 rounded-xl border-2 border-primary/30 bg-primary/5 p-3 sm:grid-cols-3">
          <Field label="Pack name"><input data-testid="pack-name" className={inp} value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} placeholder="Sand Wash Basin HMA Pack" /></Field>
          <Field label="HMA name (locks horses)"><input data-testid="pack-hma" className={inp} value={np.hma} onChange={(e) => setNp({ ...np, hma: e.target.value })} placeholder="Sand Wash Basin" /></Field>
          <Field label="Body model (optional)"><input className={inp} value={np.body_model} onChange={(e) => setNp({ ...np, body_model: e.target.value })} placeholder="standard" /></Field>
          <Field label="Price USD (blank = coins only)"><input data-testid="pack-usd" className={inp} type="number" step="0.01" value={np.usd} onChange={(e) => setNp({ ...np, usd: e.target.value })} placeholder="4.99" /></Field>
          <Field label="Coin price"><input className={inp} type="number" value={np.coin_price} onChange={(e) => setNp({ ...np, coin_price: e.target.value })} /></Field>
          <Field label="Appearance rate"><input className={inp} type="number" step="0.01" value={np.appearance_rate} onChange={(e) => setNp({ ...np, appearance_rate: e.target.value })} /></Field>
          <Field label="Max per selection"><input className={inp} type="number" value={np.max_per_selection} onChange={(e) => setNp({ ...np, max_per_selection: e.target.value })} /></Field>
          <Field label="Allow duplicate coats"><select className={inp} value={np.allow_dupe_coats ? "1" : "0"} onChange={(e) => setNp({ ...np, allow_dupe_coats: e.target.value === "1" })}><option value="0">No</option><option value="1">Yes</option></select></Field>
          <button data-testid="pack-create" disabled={busy} onClick={create} className="col-span-2 rounded-lg bg-primary px-3 py-2 text-sm font-bold text-primary-foreground sm:col-span-3">{busy ? "Creating…" : "Create Pack"}</button>
        </div>
      )}

      {packs === null ? <div className="flex items-center gap-2 py-6 text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading packs…</div>
        : packs.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No custom packs yet. Create your first one above.</p>
          : <div className="space-y-2">{packs.map((p) => <PackCard key={p.id} pack={p} onChange={load} />)}</div>}
    </div>
  );
};
